import React, { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { makeStyles, tokens } from "@fluentui/react-components";

const useStyles = makeStyles({
  resting: {
    cursor: "pointer",
    borderRadius: "3px",
    padding: "1px 3px",
    margin: "-1px -3px",
    transition: "background-color 0.15s ease, box-shadow 0.15s ease",
    display: "inline-block",
    maxWidth: "100%",
    minWidth: "24px",
    verticalAlign: "baseline",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    "@media print": {
      padding: 0,
      margin: 0,
      backgroundColor: "transparent !important",
      boxShadow: "none !important",
      cursor: "default !important",
    },
  },
  restingEmpty: {
    color: tokens.colorNeutralForeground3,
  },
  activeInput: {
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
    color: "inherit",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    borderRadius: "3px",
    padding: "1px 4px",
    margin: "-2px -5px",
    width: "calc(100% + 10px)",
    boxSizing: "border-box",
    outline: "none",
    boxShadow: `0 0 0 2px ${tokens.colorBrandBackground2}`,
    "@media print": {
      border: "none !important",
      boxShadow: "none !important",
      padding: 0,
      margin: 0,
      backgroundColor: "transparent !important",
    },
  },
  activeTextarea: {
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
    color: "inherit",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    borderRadius: "3px",
    padding: "3px 5px",
    margin: "-4px -6px",
    width: "calc(100% + 12px)",
    minHeight: "44px",
    resize: "vertical",
    boxSizing: "border-box",
    outline: "none",
    boxShadow: `0 0 0 2px ${tokens.colorBrandBackground2}`,
    "@media print": {
      border: "none !important",
      boxShadow: "none !important",
      padding: 0,
      margin: 0,
      backgroundColor: "transparent !important",
      resize: "none",
    },
  },
});

export interface InlineTextProps {
  value: string;
  onCommit: (newValue: string) => void;
  onCancel?: () => void;
  onEnterNext?: () => void;
  onEmptyEnter?: () => void;
  onTabNext?: (shiftKey: boolean) => void;
  placeholder?: string;
  multiline?: boolean;
  readOnly?: boolean;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  emptyText?: string;
  autoSelect?: boolean;
  isInitialEditing?: boolean;
  as?: "span" | "h1" | "h2" | "div";
}

export function InlineText({
  value,
  onCommit,
  onCancel,
  onEnterNext,
  onEmptyEnter,
  onTabNext,
  placeholder = "입력",
  multiline = false,
  readOnly = false,
  className,
  style,
  ariaLabel,
  emptyText = "—",
  autoSelect = false,
  isInitialEditing = false,
  as = "span",
}: InlineTextProps) {
  const styles = useStyles();
  const [isEditing, setIsEditing] = useState(isInitialEditing);
  const [currentText, setCurrentText] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setCurrentText(value);
  }, [value]);

  useEffect(() => {
    if (isInitialEditing) {
      setIsEditing(true);
    }
  }, [isInitialEditing]);

  useEffect(() => {
    if (isEditing) {
      if (multiline && textareaRef.current) {
        textareaRef.current.focus();
        if (autoSelect) textareaRef.current.select();
        // auto-resize height
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 4}px`;
      } else if (!multiline && inputRef.current) {
        inputRef.current.focus();
        if (autoSelect) inputRef.current.select();
      }
    }
  }, [isEditing, multiline, autoSelect]);

  const commit = (textToCommit?: string) => {
    const finalVal = textToCommit !== undefined ? textToCommit : currentText;
    setIsEditing(false);
    if (finalVal !== value) {
      onCommit(finalVal);
    }
  };

  const cancel = () => {
    setCurrentText(value);
    setIsEditing(false);
    onCancel?.();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // IME composition safeguard for Korean/CJK input
    if (e.nativeEvent.isComposing || (e.nativeEvent as any).isComposing || e.keyCode === 229) {
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      if (!currentText.trim() && onEmptyEnter) {
        setIsEditing(false);
        onEmptyEnter();
        return;
      }
      cancel();
      return;
    }

    if (e.key === "Tab") {
      if (onTabNext) {
        e.preventDefault();
        commit();
        onTabNext(e.shiftKey);
        return;
      }
    }

    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      const val = currentText;
      if (!val.trim() && onEmptyEnter) {
        setIsEditing(false);
        onEmptyEnter();
        return;
      }
      commit(val);
      onEnterNext?.();
      return;
    }

    if (multiline && e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      commit();
      return;
    }
  };

  if (readOnly) {
    const ComponentTag = as;
    return (
      <ComponentTag className={className} style={style} aria-label={ariaLabel}>
        {value.trim() ? value : emptyText}
      </ComponentTag>
    );
  }

  if (isEditing) {
    if (multiline) {
      return (
        <textarea
          ref={textareaRef}
          value={currentText}
          aria-label={ariaLabel}
          placeholder={placeholder}
          className={`${styles.activeTextarea} ${className || ""}`}
          style={style}
          onChange={(e) => {
            setCurrentText(e.target.value);
            e.target.style.height = "auto";
            e.target.style.height = `${e.target.scrollHeight + 4}px`;
          }}
          onBlur={() => {
            if (!currentText.trim() && onEmptyEnter) {
              setIsEditing(false);
              onEmptyEnter();
              return;
            }
            commit();
          }}
          onKeyDown={handleKeyDown}
        />
      );
    }

    return (
      <input
        ref={inputRef}
        type="text"
        value={currentText}
        aria-label={ariaLabel}
        placeholder={placeholder}
        className={`${styles.activeInput} ${className || ""}`}
        style={style}
        onChange={(e) => setCurrentText(e.target.value)}
        onBlur={() => {
          if (!currentText.trim() && onEmptyEnter) {
            setIsEditing(false);
            onEmptyEnter();
            return;
          }
          commit();
        }}
        onKeyDown={handleKeyDown}
      />
    );
  }

  const isEmpty = !value.trim();
  const ComponentTag = as;

  return (
    <ComponentTag
      role="button"
      tabIndex={0}
      aria-label={ariaLabel || "내용"}
      className={`${styles.resting} ${isEmpty ? styles.restingEmpty : ""} ${className || ""}`}
      style={style}
      onClick={() => setIsEditing(true)}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsEditing(true);
        }
      }}
    >
      {isEmpty ? (placeholder || emptyText) : value}
    </ComponentTag>
  );
}
